import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Palette } from "lucide-react";

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-sky-400 focus:ring-2 focus:ring-sky-100 outline-none";
const Lbl = ({ children }) => <label className="block text-xs font-medium text-slate-600 mb-1">{children}</label>;
const FIELDS = [["name", "Profile label (internal)"], ["company", "Company name"], ["tagline", "Tagline"], ["abn", "ABN"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["website", "Website"], ["logo_url", "Logo URL"]];
const DOC_TYPES = [["water_test", "Water Test Report"], ["quote", "Quote"], ["work_order", "Work Order Invoice"], ["receipt", "POS Receipt"]];
const EMPTY = { name: "", company: "", tagline: "", abn: "", phone: "", email: "", address: "", website: "", logo_url: "", colour: "#0891B2" };

export default function BrandingPanel({ branding, setBranding, saveBranding, onChanged }) {
  const [profiles, setProfiles] = useState([]);
  const [mapping, setMapping] = useState({});
  const [modal, setModal] = useState(null); // profile being edited or {} for new
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    const [ps, db] = await Promise.all([api("GET", "/brand-profiles"), api("GET", "/doc-brands")]);
    setProfiles(ps); setMapping(db.mapping || {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setModal({}); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setModal(p); };
  const saveProfile = async (e) => {
    e.preventDefault();
    const body = { ...form };
    if (modal.id) await api("PATCH", `/brand-profiles/${modal.id}`, body);
    else await api("POST", "/brand-profiles", body);
    toast.success("Brand profile saved");
    setModal(null); load(); onChanged && onChanged();
  };
  const delProfile = async (p) => { if (!window.confirm(`Delete brand "${p.name}"?`)) return; await api("DELETE", `/brand-profiles/${p.id}`); load(); onChanged && onChanged(); };
  const assign = async (docType, pid) => {
    const next = { ...mapping, [docType]: pid || null };
    setMapping(next);
    await api("PUT", "/doc-brands", next);
    toast.success("Document brand updated");
    onChanged && onChanged();
  };

  return (
    <div data-testid="branding-panel" className="max-w-3xl">
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Branding & document brands</h1>
      <p className="text-sm text-slate-500 mb-6">Create a brand for each business you trade as (e.g. Glamour Pools and Spas, Just Spas Newcastle), then choose which brand prints on each document. White-label ready for reselling this software.</p>

      {/* document assignments */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 mb-6">
        <p className="font-mono2 text-[10px] uppercase tracking-widest text-sky-700 mb-3">Which brand prints on each document</p>
        <div className="grid sm:grid-cols-2 gap-3">
          {DOC_TYPES.map(([dt, label]) => (
            <div key={dt}>
              <Lbl>{label}</Lbl>
              <select value={mapping[dt] || ""} onChange={(e) => assign(dt, e.target.value)} data-testid={`docbrand-${dt}`} className={inp}>
                <option value="">Default branding</option>
                {profiles.map((p) => <option key={p.id} value={p.id}>{p.name || p.company}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* brand profiles */}
      <div className="flex justify-between items-center mb-3">
        <p className="font-mono2 text-[10px] uppercase tracking-widest text-sky-700">Brand profiles</p>
        <button onClick={openNew} data-testid="add-brand-profile-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors"><Plus className="h-4 w-4" /> Add brand</button>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 mb-8">
        {profiles.length === 0 && <p className="text-sm text-slate-400">No brand profiles yet — add one for each business you trade as.</p>}
        {profiles.map((p) => (
          <div key={p.id} data-testid="brand-profile-card" className="rounded-2xl border border-slate-200 bg-white p-4 flex items-start gap-3">
            <span className="h-11 w-11 rounded-xl flex items-center justify-center text-white font-display font-bold shrink-0" style={{ background: p.colour || "#0891B2" }}>{p.logo_url ? <img src={p.logo_url} alt="" className="h-full w-full object-contain rounded-xl" /> : (p.company || p.name || "A")[0]}</span>
            <div className="min-w-0 flex-1">
              <p className="font-display font-bold text-slate-900 truncate">{p.name}</p>
              <p className="text-xs text-slate-500 truncate">{p.company}{p.abn ? ` · ABN ${p.abn}` : ""}</p>
              <p className="text-[11px] text-slate-400 truncate">{p.phone} {p.email}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => openEdit(p)} data-testid="edit-brand-profile-btn" className="p-1.5 rounded-lg text-slate-400 hover:text-sky-600 hover:bg-slate-100"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => delProfile(p)} className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>

      {/* default fallback branding */}
      <form onSubmit={saveBranding} className="rounded-2xl border border-slate-200 bg-white p-5 max-w-lg" data-testid="branding-form">
        <p className="font-display font-bold text-slate-900 mb-1">Default branding</p>
        <p className="text-xs text-slate-500 mb-4">Used for any document with no specific brand assigned.</p>
        {[["company", "Company name"], ["tagline", "Tagline"], ["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["logo_url", "Logo URL"]].map(([k, l]) => (
          <div key={k}><Lbl>{l}</Lbl><input className={inp} value={branding[k] || ""} onChange={(e) => setBranding({ ...branding, [k]: e.target.value })} /></div>
        ))}
        <Lbl>Brand colour</Lbl>
        <input type="color" value={branding.colour || "#0891B2"} onChange={(e) => setBranding({ ...branding, colour: e.target.value })} className="h-10 w-20 rounded border border-slate-200" />
        <div><button data-testid="branding-save-btn" className="mt-5 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-6 py-2.5 transition-colors">Save default branding</button></div>
      </form>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="brand-profile-modal">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto">
            <div className="flex justify-between items-center p-4 border-b border-slate-100 sticky top-0 bg-white"><span className="font-display font-bold text-slate-900 flex items-center gap-2"><Palette className="h-4 w-4 text-sky-600" /> {modal.id ? "Edit brand" : "New brand"}</span><button onClick={() => setModal(null)} className="rounded-full border border-slate-200 p-1.5 hover:bg-slate-50"><X className="h-4 w-4" /></button></div>
            <form onSubmit={saveProfile} className="p-5 grid grid-cols-2 gap-3">
              {FIELDS.map(([k, l]) => (
                <div key={k} className={k === "address" || k === "name" ? "col-span-2" : ""}><Lbl>{l}</Lbl><input className={inp} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`brand-field-${k}`} /></div>
              ))}
              <div><Lbl>Brand colour</Lbl><input type="color" value={form.colour || "#0891B2"} onChange={(e) => setForm({ ...form, colour: e.target.value })} className="h-10 w-20 rounded border border-slate-200" /></div>
              <div className="col-span-2"><button data-testid="save-brand-profile-btn" className="mt-2 w-full rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold py-2.5 transition-colors">{modal.id ? "Save changes" : "Create brand"}</button></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
